/**
 * One session, two refreshers. The web layer (auth-js) and Swift's position publisher hold the same refresh token, and
 * Swift refreshes on its own when its access token runs out while the web view is suspended - a phone in a pocket for
 * a show. The server lets a refresh token one replacement old be used again, but refuses one two replacements old
 * (refresh_token_already_used, prii-harness 2026-10-31), and auth-js signs you out on that refusal. Sim phones
 * (scripts/sim, token-expiry) reproduced it: two of Swift's refreshes in a pocket, then the wake signed Jo out.
 *
 * So every refresh the web layer makes first asks Swift whether it has refreshed on its own since the web layer last
 * gave it tokens (LynxLocation.getPublishTokens). If it has, for this same person, the refresh goes out with Swift's
 * refresh token - the newest in the chain - and its result goes straight back down to Swift (updatePublishTokens, which
 * also clears Swift's copy). auth-js then stores the new session as from any refresh. Done here, in the fetch, because
 * auth-js refreshes from several places (its ticker, getSession, the resume) and this is the one they all go through.
 *
 * No import of supabaseClient: a leaf module, so it can be unit-tested (nativeSessionHandoff.test.mjs).
 */

/** The refresh-token grant (auth-js POSTs { refresh_token } here). */
export function isRefreshTokenRequest(input) {
  try {
    const url = typeof input === 'string' ? input : (input?.url ?? String(input));
    return url.includes('/auth/v1/token') && url.includes('grant_type=refresh_token');
  } catch {
    return false;
  }
}

/** How long a refresh waits for Swift's answer before going ahead with the web layer's own token. */
export const NATIVE_ASK_TIMEOUT_MS = 1500;

/**
 * @param {typeof fetch} baseFetch
 * @param {{
 *   getNativeSession: () => Promise<{ accessToken?: string, refreshToken?: string, userId?: string } | null>,
 *   currentUserId: () => string | null,
 *   giveNativeSession: (tokens: { accessToken: string, refreshToken: string }) => Promise<unknown>,
 *   log?: (event: string, payload: object) => void,
 * }} native
 */
export function withNativeSessionHandoff(baseFetch, native) {
  return async (input, init = {}) => {
    if (!isRefreshTokenRequest(input) || typeof init.body !== 'string')
      return baseFetch(input, init);
    let body;
    try {
      body = JSON.parse(init.body);
    } catch {
      return baseFetch(input, init);
    }
    // Bounded: a native call that never answers must not hold up the refresh (auth-js waits on it under its lock).
    const theirs = await Promise.race([
      native.getNativeSession(),
      new Promise((resolve) => setTimeout(() => resolve(null), NATIVE_ASK_TIMEOUT_MS)),
    ]).catch(() => null);
    const userId = native.currentUserId();
    if (
      !theirs?.refreshToken ||
      theirs.refreshToken === body.refresh_token ||
      !userId ||
      theirs.userId !== userId
    ) {
      return baseFetch(input, init);
    }
    native.log?.('auth.native_session_adopted', {});
    const response = await baseFetch(input, {
      ...init,
      body: JSON.stringify({ ...body, refresh_token: theirs.refreshToken }),
    });
    if (response?.ok) {
      try {
        const json = await response.clone().json();
        if (json?.access_token && json?.refresh_token) {
          // Not awaited: the refresh's answer goes to auth-js now, whatever the native side does.
          void Promise.resolve()
            .then(() =>
              native.giveNativeSession({
                accessToken: json.access_token,
                refreshToken: json.refresh_token,
              }),
            )
            .catch(() => {});
        }
      } catch {
        /* auth-js reads the body itself; this was only the hand-back */
      }
    }
    return response;
  };
}
